import { useEffect, useRef, useState, type ReactNode } from "react";
import { SkeletonFrame } from "./ui/Skeleton";
import { api } from "../api";
import type { BootstrapResponse, SettlementBatchSummary, SettlementRecord } from "../types";
import { settlementTotals, shortDateTime, today, type SettlementTab } from "../payrollPresentation";
import { PlanningPayrollSelectionBar, selectionReady } from "./PlanningPayrollSelectionBar";
import { WorkerPerformance } from "./WorkerPerformance";
import { PayrollRecords, type ColumnFilters } from "./PayrollRecords";
import { PayrollBatches } from "./PayrollBatches";
import { PayrollEntryEditor } from "./PayrollEntryEditor";
import { PayrollAdjustmentEditor } from "./PayrollAdjustmentEditor";
import { PlanningPayrollBatchCreation } from "./PlanningPayrollBatchCreation";
import { PayrollSmsLoader } from "./PayrollSmsLoader";
import { type ColumnFilterValues, usePayrollData, type SettlementSort } from "./usePayrollData";
import type { MonthRange } from "./PeriodFilter";
import { useDraftLeaveGuard } from "./useDraftLeaveGuard";
import { Button } from "./ui/Button";
import { Field } from "./ui/Field";
import { Select } from "./Select";
import { Feedback, Panel } from "./ui/Surface";
import "./ui/Chip.css";
import "./PayrollManagement.css";

export type { SettlementTab } from "../payrollPresentation";
type Creation = { name: string; ids: string[]; amount: number };

/* 업무위탁 계약서는 그 사람과 회사 사이의 문서라 정산 한 줄에 딸린 첨부가 아니다.
   작업자 상세의 민감정보로 옮길 때까지 PayrollAttachments는 연결하지 않는다. */
type SettlementProps = { data: BootstrapResponse; actorName: string; range: MonthRange };
/** 창이 보여 주는 둘. 완료 버튼이 그 경계다 — 누르기 전은 예정, 누른 뒤는 내역. */
/* 성과까지 한 벌이다. 성과는 "이 기간이 어땠나"를 읽고 정산 둘은 "줄을 골라 묶는다"인데,
   셋 다 같은 기간·같은 사람으로 좁혀 보는 같은 자리라 창을 따로 띄울 일이 아니었다. */
/* 이 창이 맡는 것은 표 둘뿐이다 — 성과는 판 오른쪽 서랍 카드로 나갔다. */
type Stage = "due" | "done";

/**
 * 고를 수 있는 값은 고르면 뭔가 나오는 값이어야 한다. 그래서 목록을 명부가 아니라 지금 표에
 * 실제로 찍힌 값에서 뽑는다 — 표는 이미 다른 열의 조건을 다 거친 결과라, 프로젝트를 고르면
 * 작업 목록이 그 프로젝트 것만 남고 작업자도 그 프로젝트에서 일한 사람만 남는다.
 */
/* 두 목록을 합친다. 같은 열에 둘 다 값을 주면 뒤엣것이 앞엣것을 통째로 덮어써서, 표에
   남은 줄에서 뽑은 짧은 목록이 전체 명단을 밀어냈다. 같은 값은 한 번만 둔다. */
const mergeOptions = (
  base: Record<string, Array<{ value: string; label: string }>>,
  extra: Record<string, Array<{ value: string; label: string }>>,
) => {
  const merged: Record<string, Array<{ value: string; label: string }>> = { ...base };
  for (const [key, rows] of Object.entries(extra)) {
    const seen = new Map((merged[key] ?? []).map(row => [row.value, row]));
    for (const row of rows) if (!seen.has(row.value)) seen.set(row.value, row);
    merged[key] = [...seen.values()];
  }
  return merged;
};

const ledgerNames = (records: SettlementRecord[]) => {
  const pull = (pick: (record: SettlementRecord) => string | null | undefined) =>
    [...new Set(records.map(pick).filter((value): value is string => !!value))]
      .sort((a, b) => a.localeCompare(b, "ko-KR")).map(value => ({ value, label: value }));
  const workers = new Map(records.map(record => [record.workerId, record.workerName]));
  return {
    projectName: pull(record => record.projectName),
    subtaskName: pull(record => record.subtaskName),
    workerId: [...workers].sort((a, b) => a[1].localeCompare(b[1], "ko-KR")).map(([value, label]) => ({ value, label })),
  };
};

/**
 * 수동 등록의 구분은 "이 돈이 어떻게 계산되나"다. 작업자 등록의 구분(도급제·기간제·단기알바·
 * 업무위탁)은 "이 사람은 누구인가"를 묻는 다른 질문이라 같은 말을 쓰지 않는다 — 기간제인
 * 분이 월급을 받든 도급을 받든 금액은 아래 셋 중 하나로 계산된다.
 *
 *   도급  수량 × 단가      월급  정액      시급  시간 × 시급
 *
 * 그래서 계약 폼 안에 있던 "계산 방식"(정액 / 수량×단가)도 사라진다. 구분이 이미 정했다.
 */
/* 공제·추가는 갈래가 아니다 — 공제를 받는 사람도 결국 도급이거나 월급이거나 시급이다.
   금액이 어디서 나왔는지를 묻는 이 자리에 "공제"가 서면 층이 다른 말이 같은 줄에 선다.
   공제·추가 지급도 같은 문으로 들어온다 — 어느 작업 건에도 매이지 않는 돈이라, 줄에
   얹지 않고 제 줄로 선다. */
/* 손으로 정산 줄 하나를 만드는 길은 다섯 갈래다. 앞의 셋은 일한 값을 셈해서 만들고(단가 ×
   수량, 월급, 시급), 뒤의 둘은 셈 없이 금액 하나를 적는다 — 지난달 누락분처럼 어느 작업
   건에도 매이지 않는 돈이다. 하는 일이 같으므로 문도 하나다. */
type RegisterKind = "piece" | "monthly" | "hourly" | "deduction" | "addition";

/* claude.ai Skills 제목 줄 오른쪽 아이콘 단추 실측: 32×32 · r8 · 투명 · #52514E · 아이콘 20. */
/* 묶음 CTA 는 + 가 아니라 겹친 장(묶음) 아이콘 — + 는 직접 추가 몫이다. */
const stackIcon = <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"><path d="M10 3.5 3.5 7 10 10.5 16.5 7z" /><path d="m3.5 10 6.5 3.5 6.5-3.5" /><path d="m3.5 13 6.5 3.5 6.5-3.5" /></svg>;
const plusIcon = <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M10 4.5v11M4.5 10h11" /></svg>;

function BatchCta({ disabled, name, suggestedName, onName, onCreate }: { disabled: boolean; name: string; suggestedName: string; onName: (value: string) => void; onCreate: () => void }) {
  const [editing, setEditing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) input.current?.focus(); }, [editing]);
  useEffect(() => { if (disabled) setEditing(false); }, [disabled]);
  if (!editing) return <button type="button" className="payroll-batch-cta" disabled={disabled} data-testid="create-settlement-batch"
    onClick={() => setEditing(true)}>{stackIcon}<span>정산 묶음</span></button>;
  return <input ref={input} className="payroll-batch-cta-input" value={name} maxLength={100} placeholder={suggestedName || "묶음명"}
    aria-label="묶음 이름" data-testid="settlement-batch-name"
    onChange={event => onName(event.target.value)}
    onKeyDown={event => {
      if (event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); setEditing(false); onCreate(); }
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setEditing(false); }
    }}
    onBlur={() => { if (!name.trim()) setEditing(false); }} />;
}

export function PlanningPayrollWorkspace(props: SettlementProps & {
  /* 표를 한 사람으로 좁힌다. 좁히면 카드 제목의 합계도 같은 사람 것이 되어야 하므로, 고르는
     일은 페이지가 쥐고 여기는 결과만 받는다. */
  columnFilters?: ColumnFilterValues;
  workerFilter?: ColumnFilters;
  /* 어느 쪽을 볼지 바깥에서 정해 줄 수 있다 — 요약 카드의 손잡이가 창을 열면서 함께 말한다. */
  stage?: Stage;
  onStage?: (stage: Stage) => void;
  /* 성과 칸의 기간 칩. 기간을 쥔 쪽이 만들어 내려 준다 — 이 화면은 범위를 받아 읽기만 한다. */
  performanceExtra?: ReactNode;
  onSelectWorker?: (id: string) => void;
  /* 겹친 창을 한 번에 걷는 법. 안쪽 창의 X가 쓴다. */
  onCloseAll?: () => void;
}) {
  const { data, actorName, range, workerFilter } = props;
  const [localFilters, setLocalFilters] = useState<ColumnFilterValues>({});
  const columnFilters = { ...props.columnFilters, ...localFilters };
  /* 구분은 표의 열이 말한다. 필터에서 빼는 대신 질의는 늘 전체 구분을 가져온다. */
  const activeTab = "all" as const;
  /* 정산에는 세 상태가 있고 코드에도 이름이 붙어 있다 — 미정산 · 정산 대기 · 정산 완료.
     앞의 둘은 아직 되돌릴 수 있어 "할 일"이고 마지막은 지나간 일이다. 한 표에 섞어 두면
     절반의 체크박스가 죽어 있는 이유가 화면 어디에도 안 적힌다. */
  const [ownStage, setOwnStage] = useState<Stage>("due");
  /* 바깥에서 정해 줄 수도 있다 — 요약 카드의 "정산 예정 / 정산 내역" 손잡이가 창을 열면서
     어느 쪽을 볼지 함께 말한다. */
  const stage = props.stage ?? ownStage;
  const setStage = props.onStage ?? setOwnStage;
  /* 묶은 순간 "내역"이다 — 묶음이 떠 있는데 그 안의 줄이 예정 쪽에 남아 있으면, 내역을
     열어도 "없습니다"가 뜨고 방금 묶은 것이 어디 갔는지 찾아야 한다. 돈이 나가는 때(paid)는
     그 다음 단계일 뿐이고, 되돌릴 수 있는지는 묶음 카드의 단추가 말한다. */
  const stageStatus = stage === "done" ? (["batched", "paid"] as const) : (["pending"] as const);
  const [selectedBatch, setSelectedBatch] = useState<SettlementBatchSummary | null>(null);
  /* 바깥(설정 창 제목 옆 세그먼트)에서 단계를 바꿔도 안쪽 칩과 똑같이 고른 묶음을 비운다 — 내역에서
     고른 묶음이 남은 채 예정으로 오면 예정 표가 "그 묶음 안의 예정"(0건)으로 걸러져 통째로 비었다. */
  useEffect(() => { setSelectedBatch(null); }, [stage]);
  /* 방금 만든 묶음은 만들자마자 눌린 채로 선다 — 만들고 나면 그 묶음에 뭐가 들었는지를
     바로 보고 싶은데, 아무것도 안 골라진 채로 돌아오면 "거른 조건에 맞는 내역이 없습니다"가
     뜨고 방금 한 일이 어디로 갔는지 찾아야 한다. 목록이 다시 올 때 그 id로 집어 고른다. */
  const [createdBatchId, setCreatedBatchId] = useState<string | null>(null);
  const [sort, setSort] = useState<SettlementSort>("work_time");
  /* 열을 고르는 일과 방향을 뒤집는 일은 다른 값이다. 같은 머리글을 다시 누르면 방향만
     뒤집히고, 다른 머리글을 누르면 그 열의 기본 방향으로 돌아간다 — 열을 바꿀 때까지
     방향이 따라오면 "작업자를 눌렀더니 거꾸로 뜬다"가 된다. */
  const [descending, setDescending] = useState(false);
  /* 친 글자는 바로 질의로 가지 않는다 — 한 글자마다 서버를 부르면 "강"을 치는 동안 세 번
     오가고, 그 셋이 돌아오는 순서가 엇갈리면 다 친 뒤에 첫 글자의 결과가 덮어쓴다. */
  const [typed, setTyped] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => { const timer = window.setTimeout(() => setQuery(typed), 220); return () => window.clearTimeout(timer); }, [typed]);
  const chooseSort = (next: typeof sort) => {
    if (next === sort) { setDescending(current => !current); return; }
    setSort(next); setDescending(false);
  };
  const state = usePayrollData(activeTab, range, selectedBatch?.id, sort === "worker" && descending ? "worker_desc" : sort === "work_time" && descending ? "work_time_asc" : sort, columnFilters, stageStatus, false, undefined, undefined, 20, true, query);
  const [selectionState, setSelection] = useState<{ tab: SettlementTab; records: SettlementRecord[] }>({ tab: activeTab, records: [] });
  const selection = selectionState.tab === activeTab ? selectionState.records : [];
  /* 이름은 "오늘이 몇 월인가"가 아니라 "무엇을 정산하는가"에서 나온다. 8월 작업을 9월에
     정산하면 오늘 기준 이름은 "2026년 09월 묶음"이 되어, 8월 칩에서 9월 묶음이 뜨는 것처럼
     보였다. 고른 기록의 정산일을 따른다. */
  /* 묶음 이름은 미리 적어 둔다 — 고른 줄의 달에서 나온 이름이고, 같은 달에 이미 만든 묶음이
     있으면 뒤에 차수를 붙인다. 손으로 적게 두면 매번 같은 이름을 치거나(그래서 목록에 같은
     이름이 쌓이거나) 비워 둔 채 만들게 된다. 적어 둔 이름이 마음에 안 들면 고치면 된다.
     "묶음"이라는 말은 빼고 "N차"로 끝낸다 — 여기 서 있는 것이 이미 묶음이라, 이름에 다시
     적으면 목록에서 "10월 정산 묶음"이 두 줄 나란히 서고 차수만 눈으로 찾게 된다. */
  const suggestedName = (() => {
    const months = [...new Set(selection.map(record => record.settlementDate.slice(0, 7)))].sort();
    const base = months.length === 1 ? months[0] : range?.from.slice(0, 7) ?? today().slice(0, 7);
    const label = `${Number(base.slice(0, 4))}년 ${Number(base.slice(5, 7))}월 정산`;
    const taken = state.batches.items.filter(batch => batch.name.startsWith(label)).length;
    return `${label} ${taken + 1}차`;
  })();
  const [name, setName] = useState(""), [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(null);
  /* 고치는 줄. 고치는 창은 만드는 창과 같은 것을 쓴다 — 같은 값을 적는 자리가 둘이면
     한쪽만 고쳐질 날이 온다. */
  const [editing, setEditing] = useState<SettlementRecord | null>(null)
  const [register, setRegister] = useState<RegisterKind | null>(null), [creation, setCreation] = useState<Creation | null>(null), [completing, setCompleting] = useState(""), [sms, setSms] = useState<SettlementBatchSummary | null>(null);
  const [downloading, setDownloading] = useState(false), downloadGuard = useRef(false), root = useRef<HTMLDivElement>(null), returnFocus = useRef<HTMLElement | null>(null), retry = useRef<HTMLButtonElement>(null);
  const completionGuard = useRef(false), completedIds = useRef(new Set<string>());
  const writeDisabled = state.loading || !!state.error || !!creation || downloading || !!completing;
  const emptyText = state.loading || state.paging.records?.loading ? "목록을 불러오고 있습니다…" : state.error ? "목록을 다시 확인해 주세요." : undefined;
  /* 나갈 때 되묻지 않는다. 표에서 몇 줄 고른 것은 버려도 다시 고르면 그만이고, 이 화면의
     다른 동작들도 확인 없이 바로 처리한다. 묶음을 만드는 중일 때만 막는다. */
  useDraftLeaveGuard({ dirty: false, busy: downloading || !!creation });
  useEffect(() => { setSelection({ tab: activeTab, records: [] }); setSelectedBatch(null); setNotice(null); }, [range?.from, range?.to]);
  const select = (ids: string[]) => { const records = new Map([...selection, ...state.records.items].map(record => [record.id, record])); setSelection({ tab: activeTab, records: [...new Set(ids)].flatMap(id => records.has(id) ? [records.get(id)!] : []) }); };
  const remember = () => { returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; setNotice(null); };
  const restoreFocus = () => state.error ? retry.current : returnFocus.current?.isConnected && !returnFocus.current.matches(":disabled") ? returnFocus.current : root.current?.querySelector<HTMLButtonElement>('.payroll-source-filter .ui-chip.selected') ?? null;
  const notify = (text: string, error?: boolean) => setNotice({ text, error });
  const download = async (batch: SettlementBatchSummary) => { if (downloadGuard.current) return; downloadGuard.current = true; setDownloading(true); setNotice(null); try { await api.downloadSettlementTransferFile(batch.id); } catch (cause) { notify(cause instanceof Error ? cause.message : "이체 파일을 내려받지 못했습니다.", true); } finally { downloadGuard.current = false; setDownloading(false); } };
  /* 정산 완료에는 확인 창을 두지 않는다. 버튼 이름이 이미 하는 일을 말하고, 막아야 할
     조건(문자 없음·수신번호 오류)은 서버가 같은 자리에서 검증한다. 막히면 알림으로 답한다. */
  const completeBatch = async (batch: SettlementBatchSummary) => {
    if (completionGuard.current) return;
    completionGuard.current = true;
    setCompleting(batch.id); setNotice(null);
    try {
      if (!completedIds.current.has(batch.id)) {
        await api.completeSettlementBatch(batch.id, { completedBy: actorName });
        completedIds.current.add(batch.id);
      }
      try { await state.reload(); }
      catch { notify("정산은 완료됐지만 최신 목록을 불러오지 못했습니다. 목록을 다시 확인해 주세요.", true); }
    } catch (cause) { notify(cause instanceof Error ? cause.message : "정산을 완료하지 못했습니다.", true); }
    finally { completionGuard.current = false; setCompleting(""); }
  };
  /* 미정산 한 줄을 지우는 일이라 확인 창을 두지 않는다. 이미 묶인 줄은 버튼이 아예 없고,
     잘못 지워도 수동 등록으로 다시 넣으면 되는 되돌릴 수 있는 일이다. 계약은 첨부 파일이
     함께 사라지므로 그것만 한 번 묻는다. */
  const deleteRecord = async (record: SettlementRecord) => {
    if (record.source === "contract" && record.recordKind === "base"
      && !window.confirm(`${record.title}을(를) 삭제하면 연결된 계약 파일도 함께 사라집니다.\n삭제할까요?`)) return;
    setNotice(null);
    try {
      await (record.recordKind === "manual" ? api.deleteManualSettlement(record.id)
        : record.recordKind === "adjustment" ? api.deleteSettlementAdjustment(record.id)
        : record.source === "part_time" ? api.deletePartTimeSettlement(record.referenceId)
        : api.deleteSettlementContract(record.referenceId));
      select(selection.filter(item => item.id !== record.id).map(item => item.id));
      await state.reload();
    } catch (cause) { notify(cause instanceof Error ? cause.message : "정산 내역을 삭제하지 못했습니다.", true); }
  };
  // 표의 쪽 번호와 서버 커서를 한곳에서 관리한다. 마지막 쪽으로 건너뛰어도
  // 필요한 커서를 모두 따라가며, 필터/정렬/묶음이 바뀌면 즉시 첫 쪽으로 돌아간다.
  const [recordPage, setRecordPage] = useState({ key: "", page: 1 });
  const [pageSize, setPageSize] = useState(20);
  const page = recordPage.key === state.key ? recordPage.page : 1;
  const total = state.records.total ?? state.records.items.length + (state.records.nextCursor ? 1 : 0);
  const needsMore = !!state.records.nextCursor && state.records.items.length < Math.min(page * pageSize, total);
  useEffect(() => {
    if (!needsMore || state.loading || state.paging.records?.loading || state.paging.records?.error) return;
    void state.loadMore("records");
  }, [needsMore, state.key, state.loading, state.paging.records?.loading, state.paging.records?.error, state.records.items.length]);
  /* 이름은 미리 채우지 않는다 — 채워 두면 그것이 "이미 적은 이름"처럼 보여, 고치려는
     사람은 지우고 다시 쳐야 하고 그냥 두려는 사람은 그게 제안인지 입력인지 모른다.
     비워 두고 자리표시로만 보여 준다: 그대로 두고 만들면 그 이름으로 간다. */
  const nameTouched = useRef(false);
  useEffect(() => { if (!selection.length) { nameTouched.current = false; setName(""); } }, [selection.length]);

  /* 목록이 새로 오면 방금 만든 묶음을 집어 고른다 — 만드는 창이 닫히는 시점에는 아직
     그 묶음이 목록에 없다(새로 받아 오는 중이다). 한 번 고르고 나면 표식은 버린다. */
  useEffect(() => {
    if (!createdBatchId) return;
    const made = state.batches.items.find(batch => batch.id === createdBatchId);
    if (!made) return;
    setStage("done");
    setSelectedBatch(made);
    setCreatedBatchId(null);
  }, [createdBatchId, state.batches.items]);
  /* 내역을 열면 첫 묶음이 눌린 채로 선다 — 묶음이 있는데 아무것도 안 골라져 있으면
     "정산 내역이 없습니다"가 뜨고, 방금 묶은 것을 보려고 칩을 한 번 더 눌러야 한다.
     고를 것이 하나뿐일 때 고르게 하는 것은 묻는 것이 아니라 일을 하나 더 시키는 것이다. */
  useEffect(() => {
    if (stage !== "done" || selectedBatch || !state.batches.items.length) return;
    setSelectedBatch(state.batches.items[0]);
  }, [stage, selectedBatch, state.batches.items]);

  const batchList = (expanded?: React.ReactNode) => <PayrollBatches renderExpanded={expanded === undefined ? undefined : () => expanded} batches={state.batches.items} selectedId={selectedBatch?.id} disabled={writeDisabled} onSelect={batch => setSelectedBatch(selectedBatch?.id === batch.id ? null : batch)} onDownload={batch => void download(batch)} onSms={batch => { remember(); setSms(batch); }} onComplete={batch => void completeBatch(batch)}
    onDelete={batch => void (async () => {
      if (!window.confirm(`${batch.name}을(를) 삭제하면 ${batch.recordCount.toLocaleString()}건이 미정산으로 돌아갑니다.\n삭제할까요?`)) return;
      try { await api.deleteSettlementBatch(batch.id); await state.reload(); }
      catch (cause) { notify(cause instanceof Error ? cause.message : "정산 묶음을 삭제하지 못했습니다.", true); }
    })()}
    onRename={(batch, name) => void (async () => {
      try { await api.renameSettlementBatch(batch.id, name); await state.reload(); }
      catch (cause) { notify(cause instanceof Error ? cause.message : "묶음명을 바꾸지 못했습니다.", true); }
    })()} />;
  /* 화면 전체가 뜰 때는 도는 고리 하나, 화면 안의 한 덩이가 늦을 때는 밑그림 — 저쪽이
     둘을 그렇게 나눠 쓴다 (2026-10-16). 여기는 화면은 이미 떠 있고 표만 늦는 자리라 밑그림이다. */
  /* 밑그림은 300ms 넘게 늦을 때만 — 묶음을 눌러 그 묶음 기록을 받는 짧은 사이에 화면만 한 밑그림이
     번쩍 떴다 사라지는 것이 더 거슬린다(2026-10-19 밤 지윤). 그 사이엔 빈 자리로 두고 값이 오면 바로 표. */
  const waiting = state.loading && !state.records.items.length;
  const [slow, setSlow] = useState(false);
  useEffect(() => { if (!waiting) { setSlow(false); return; } const timer = window.setTimeout(() => setSlow(true), 300); return () => window.clearTimeout(timer); }, [waiting]);
  const recordsTable = () => waiting ? (slow ? <SkeletonFrame /> : <div className="payroll-records-pending" aria-busy="true" />) : <><PayrollRecords toolbarLead={<>
          {stage === "due" ? <PlanningPayrollSelectionBar selected={selection} workers={data.workers} /> : null}
        </>}
        /* 머리글 거르개(작업자·프로젝트·작업 「전체」)는 이번 배포에서 걷는다(2026-11-02 지윤). 표·정산 처리·쪽 넘김은 그대로. */
        filters={undefined} emptyText={emptyText} records={state.records.items} showSource={activeTab === "all"} disabled={writeDisabled} sort={sort} descending={descending} onSort={chooseSort} onDelete={record => void deleteRecord(record)}
          remote={{ page, pageSize, total,
            onPage: next => setRecordPage({ key: state.key, page: next }),
            onPageSize: size => { setPageSize(size); setRecordPage({ key: state.key, page: 1 }); },
            sortColumn: sort === "worker" ? "workerName" : "settlementDate",
            sortDirection: sort === "worker" ? descending ? "desc" : "asc" : descending ? "asc" : "desc",
            onSort: column => chooseSort(column === "workerName" ? "worker" : "work_time"),
            loading: !!state.paging.records?.loading || (needsMore && !state.paging.records?.error),
            error: state.paging.records?.error, onRetry: () => void state.loadMore("records"),
          }}
          selection={{ workers: data.workers, selectedIds: selection.map(record => record.id), onSelect: select, onEdit: record => { remember(); setEditing(record); } }} /></>;
  return <div ref={root} className="payroll-workspace">
    <div className="payroll-content">
      {/* 알림은 오류에만 쓴다. 잘된 일은 화면이 바뀌는 것으로 이미 보인다. */}
      {notice?.error && <Feedback tone="error">{notice.text}</Feedback>}
      {downloading && <Feedback>대량이체 파일을 내려받고 있습니다…</Feedback>}
      {state.error && !register && !editing && !creation && <Feedback tone="error">{state.error} 최신 목록을 확인한 뒤 정산을 진행해 주세요. <Button ref={retry} busy={state.loading} onClick={() => void state.reload().catch(() => undefined)}>목록 다시 확인</Button></Feedback>}
      <Panel>
        {/* 탭과 찾는 칸이 맨 위 한 줄이다 — 둘 다 "무엇을 보고 있나"를 정하는 자리고, 그
            아래 줄들(묶음 · 손잡이)은 "고른 것으로 무엇을 하나"다. 찾기는 서버가 맡는다:
            받아 둔 쪽만 훑으면 다음 쪽에 있는 줄은 못 찾는다. 생김새는 표의 찾기 칸에서
            그대로 가져왔다. */}
        <div className="payroll-head-row">
          <div className="payroll-stage-chips" role="group" aria-label="정산 상태">
            {([["due", "정산 예정"], ["done", "정산 내역"]] as const).map(([value, label]) =>
              <button key={value} type="button" className={`ui-chip${stage === value ? " selected" : ""}`}
                aria-pressed={stage === value} onClick={() => { setStage(value); setSelectedBatch(null); }}>{label}</button>)}
          </div>
          <label className="payroll-head-search">
            <svg viewBox="0 0 24 24" aria-hidden="true" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="text" value={typed} onChange={event => setTyped(event.target.value)} placeholder="검색" aria-label="찾기" />
          </label>
          {/* Skills 제목 줄의 Add▾ 자리 = 직접 추가(2026-10-19 밤 지윤). 고를 갈래가 다섯이라 저쪽처럼 ▾ 메뉴로
              편다. 이체 파일 생성은 고른 줄이 있어야 하는 일이라 표 위 고르기 줄에 그대로 둔다. */}
          {/* 2026-10-19 밤 지윤: 정렬 아이콘은 두지 않는다(표 머리줄이 정렬을 맡는다). + 는 메뉴 없이 바로 수동 정산 창으로 —
              갈래(도급·월급·시급·공제·추가 지급)는 그 창 안의 「구분」에서 고른다. */}
          {stage === "due" && <button type="button" className="payroll-add-trigger payroll-add-trigger--icon" disabled={writeDisabled}
            data-testid="open-settlement-register" aria-label="직접 추가" title="직접 추가" onClick={() => { remember(); setRegister("piece"); }}>{plusIcon}</button>}
          {/* 2026-10-19 밤 지윤: 이 화면에서 가장 큰 일은 묶기다 — 검정 CTA 는 「정산 묶음」, 직접 추가는 + 아이콘.
              누르면 그 자리가 이름 칸으로 바뀌고, Enter 면 묶어 이체 파일을 받고 내역으로 넘어간다.
              비워 두고 Enter 면 고른 기록의 달 이름(자리표시에 보이는 것)으로 묶는다. */}
          {stage === "due" && <BatchCta disabled={writeDisabled || !selection.length || !selectionReady(selection, data.workers)}
            name={name} suggestedName={suggestedName} onName={value => { nameTouched.current = true; setName(value); }}
            onCreate={() => { remember(); setCreation({ name: name.trim() || suggestedName, ids: selection.map(record => record.id), amount: settlementTotals(selection).payableAmount }); }} />}
        </div>
        {/* 위에서 아래로 정산의 순서가 그대로 읽히게 둔다 — 등록·이체가 먼저고, 그렇게 만들어진
            묶음과 마지막 단계인 정산 완료가 그 아래다. 묶음은 여전히 표 바로 위라, 골라서
            좁혀 보면 결과가 바로 아래에 나타난다. */}
        {/* 등록·묶기는 표 머리줄 안으로 들어갔다 — 세 줄로 서 있던 것(손잡이 줄 · 고른 수 ·
            찾기)이 전부 "고른 줄로 하는 일"이라 한 줄에 있어야 순서가 읽힌다. */}
        {/* 이체 파일을 만들면 그 결과가 바로 아래에 줄로 선다. 화살표 하나로 잇는다 —
            말로 덧붙이면 화살표가 못 한 일을 문장이 대신하는 꼴이 된다. 가리킬 묶음이
            없을 때는 띄우지 않는다. */}
        {/* 묶음은 내역에서만 선다 — 이체하고 완료하는 대상이 곧 지나간 일의 단위다. */}

        {/* 거르개는 표 위 한 줄이다 — 열마다 ▾를 달면 지금 무엇으로 걸려 있는지가 누르기
            전에는 안 보인다. 머리줄은 정렬만 말한다. */}
        {/* 묶음을 고르면 그 안에 표가 들어가고, 안 고르면 표가 칩 줄 아래 그대로 선다 —
            고르기 전에 빈 화면을 보여 주면 "여기엔 아무것도 없다"로 읽힌다. */}
        {stage === "done" && batchList(selectedBatch ? recordsTable() : undefined)}
        {stage === "done" && !selectedBatch && recordsTable()}
        {stage === "due" && recordsTable()}
      </Panel>
    </div>
    {/* 만든 자리에서 고친다 — 같은 값을 적는 창이 둘이면 언젠가 한쪽만 고쳐진다. */}
    {editing && <PayrollAdjustmentEditor source={editing.source} record={editing} workers={data.workers} actorName={actorName}
      refresh={state.reload} onSaved={() => setEditing(null)} onClose={() => setEditing(null)} restoreFocus={restoreFocus} />}
    {register && (() => {
      /* 갈래를 바꾸면 폼이 통째로 바뀐다 — 받는 값이 서로 달라 한 폼에 겹칠 수 없다. */
      const kindPicker = <Field label="구분" required><Select className="register-kind" disabled={false} value={register} data-testid="register-kind"
        onChange={event => setRegister(event.target.value as RegisterKind)}>
        <option value="piece">도급</option>
        <option value="monthly">월급</option>
        <option value="hourly">시급</option>
        <option value="deduction">공제</option>
        <option value="addition">추가 지급</option>
      </Select></Field>;
      if (register === "deduction" || register === "addition") {
        return <PayrollAdjustmentEditor source="piecework" kind={register} kindPicker={kindPicker} workers={data.workers} actorName={actorName}
          refresh={state.reload} onSaved={() => setRegister(null)} onClose={() => setRegister(null)} restoreFocus={restoreFocus} />;
      }
      return <PayrollEntryEditor kind={register} kindPicker={kindPicker} workers={data.workers} projects={data.projects} actorName={actorName} refresh={state.reload} onSaved={() => setRegister(null)} onClose={() => setRegister(null)} restoreFocus={restoreFocus} />;
    })()}
    {creation && <PlanningPayrollBatchCreation {...creation} actorName={actorName} refresh={state.reload} onCreated={batchId => { setSelection({ tab: activeTab, records: [] }); setCreatedBatchId(batchId); }} onFinished={() => { setCreation(null); setName(""); }} onClose={() => setCreation(null)} restoreFocus={restoreFocus} />}
    {sms && <PayrollSmsLoader summary={sms} refresh={state.reload} onClose={() => setSms(null)} onCloseAll={props.onCloseAll} notify={notify} />}
  </div>;
}
